import React, { useEffect, useRef, useState } from 'react';
import { sitesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Building2, Plus, Pencil, Trash2, Search, MapPin, Phone, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Genere une URL absolue vers l'icone en ajoutant un cache-buster pour forcer
// le rechargement apres remplacement.
const buildIconSrc = (iconUrl, cacheBuster = '') => {
    if (!iconUrl) return '';
    const abs = iconUrl.startsWith('http') ? iconUrl : `${API_BASE}${iconUrl}`;
    return cacheBuster ? `${abs}?v=${cacheBuster}` : abs;
};

export default function SitesPage() {
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSite, setEditingSite] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [siteToDelete, setSiteToDelete] = useState(null);
    
    const [form, setForm] = useState({
        name: '', address: '', contact: '', icon_url: ''
    });
    const [uploadingIcon, setUploadingIcon] = useState(false);
    const [iconCacheBuster, setIconCacheBuster] = useState(Date.now());
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadSites();
    }, []);

    const loadSites = async () => {
        try {
            const response = await sitesAPI.getAll();
            const data = response.data || [];
            const order = ['génipa', 'genipa', 'cluny', 'dillon', 'océanis', 'oceanis', 'vauclin', 'siège', 'siege', 'concept', 'my food'];
            data.sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                const idxA = order.findIndex(k => nameA.includes(k));
                const idxB = order.findIndex(k => nameB.includes(k));
                return (idxA >= 0 ? idxA : 999) - (idxB >= 0 ? idxB : 999);
            });
            setSites(data);
        } catch (error) {
            toast.error('Erreur lors du chargement des sites');
        } finally {
            setLoading(false);
        }
    };

    const filteredSites = sites.filter(s =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.address?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleOpenDialog = (site = null) => {
        if (site) {
            setEditingSite(site);
            setForm({
                name: site.name,
                address: site.address || '',
                contact: site.contact || '',
                icon_url: site.icon_url || ''
            });
        } else {
            setEditingSite(null);
            setForm({ name: '', address: '', contact: '', icon_url: '' });
        }
        setIconCacheBuster(Date.now());
        setDialogOpen(true);
    };

    const handleIconFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // reset input pour permettre de re-selectionner le meme fichier
        if (fileInputRef.current) fileInputRef.current.value = '';

        if (!file.type.startsWith('image/')) {
            toast.error('Le fichier doit etre une image');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 2 Mo)');
            return;
        }

        // Pour un site en cours de creation, on doit d'abord le creer
        if (!editingSite) {
            if (!form.name) {
                toast.error('Enregistrez d\'abord le site (nom requis) avant d\'ajouter une icone');
                return;
            }
            try {
                setUploadingIcon(true);
                const createRes = await sitesAPI.create({ ...form, icon_url: '' });
                const newSite = createRes.data;
                const uploadRes = await sitesAPI.uploadIcon(newSite.id, file);
                setEditingSite({ ...newSite, icon_url: uploadRes.data.icon_url });
                setForm(prev => ({ ...prev, icon_url: uploadRes.data.icon_url }));
                setIconCacheBuster(Date.now());
                toast.success('Site cree avec icone');
                loadSites();
            } catch (error) {
                toast.error(error.response?.data?.detail || 'Erreur upload icone');
            } finally {
                setUploadingIcon(false);
            }
            return;
        }

        try {
            setUploadingIcon(true);
            const res = await sitesAPI.uploadIcon(editingSite.id, file);
            setForm(prev => ({ ...prev, icon_url: res.data.icon_url }));
            setEditingSite(prev => ({ ...prev, icon_url: res.data.icon_url }));
            setIconCacheBuster(Date.now());
            toast.success('Icone mise a jour');
            loadSites();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
        } finally {
            setUploadingIcon(false);
        }
    };

    const handleRemoveIcon = async () => {
        if (!editingSite) {
            setForm(prev => ({ ...prev, icon_url: '' }));
            return;
        }
        try {
            await sitesAPI.deleteIcon(editingSite.id);
            setForm(prev => ({ ...prev, icon_url: '' }));
            setEditingSite(prev => ({ ...prev, icon_url: '' }));
            toast.success('Icone supprimee');
            loadSites();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleSave = async () => {
        if (!form.name) {
            toast.error('Le nom est requis');
            return;
        }

        try {
            if (editingSite) {
                await sitesAPI.update(editingSite.id, form);
                toast.success('Site mis à jour');
            } else {
                await sitesAPI.create(form);
                toast.success('Site créé');
            }
            setDialogOpen(false);
            loadSites();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        }
    };

    const handleDeleteClick = (site) => {
        setSiteToDelete(site);
        setDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await sitesAPI.delete(siteToDelete.id);
            toast.success('Site supprimé');
            setDeleteDialog(false);
            loadSites();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="sites-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Sites</h1>
                    <p className="text-slate-500 mt-1">Gestion de vos sites de stockage</p>
                </div>
                <Button onClick={() => handleOpenDialog()} className="btn-primary" data-testid="add-site-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau site
                </Button>
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Rechercher par nom ou adresse..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                            data-testid="search-sites-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Sites Grid */}
            {filteredSites.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">Aucun site trouvé</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredSites.map((site) => (
                        <Card key={site.id} className="card-interactive" data-testid={`site-card-${site.id}`}>
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center overflow-hidden">
                                        {site.icon_url ? (
                                            <img
                                                src={buildIconSrc(site.icon_url, iconCacheBuster)}
                                                alt={site.name}
                                                className="w-full h-full object-cover"
                                                data-testid={`site-icon-${site.id}`}
                                            />
                                        ) : (
                                            <Building2 className="w-6 h-6 text-blue-600" />
                                        )}
                                    </div>
                                    <div className="flex gap-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleOpenDialog(site)}
                                            data-testid={`edit-site-${site.id}`}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => handleDeleteClick(site)}
                                            data-testid={`delete-site-${site.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                <h3 className="font-semibold text-lg text-slate-900 mb-3">{site.name}</h3>
                                <div className="space-y-2 text-sm text-slate-500">
                                    {site.address && (
                                        <div className="flex items-start gap-2">
                                            <MapPin className="w-4 h-4 mt-0.5" />
                                            <span className="line-clamp-2">{site.address}</span>
                                        </div>
                                    )}
                                    {site.contact && (
                                        <div className="flex items-center gap-2">
                                            <Phone className="w-4 h-4" />
                                            <span>{site.contact}</span>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingSite ? 'Modifier le site' : 'Nouveau site'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Icone du site */}
                        <div className="space-y-2">
                            <Label>Icone du site</Label>
                            <div className="flex items-center gap-4">
                                <div className="w-20 h-20 bg-blue-50 rounded-xl flex items-center justify-center overflow-hidden border border-slate-200">
                                    {form.icon_url ? (
                                        <img
                                            src={buildIconSrc(form.icon_url, iconCacheBuster)}
                                            alt="icone"
                                            className="w-full h-full object-cover"
                                            data-testid="site-icon-preview"
                                        />
                                    ) : (
                                        <Building2 className="w-8 h-8 text-blue-400" />
                                    )}
                                </div>
                                <div className="flex-1 flex flex-col gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleIconFileChange}
                                        className="hidden"
                                        data-testid="site-icon-file-input"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploadingIcon}
                                        data-testid="site-icon-upload-btn"
                                        className="w-full sm:w-auto"
                                    >
                                        <Upload className="w-4 h-4 mr-2" />
                                        {uploadingIcon ? 'Envoi en cours...' : (form.icon_url ? 'Remplacer l\'icone' : 'Choisir un fichier')}
                                    </Button>
                                    {form.icon_url && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleRemoveIcon}
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full sm:w-auto justify-start"
                                            data-testid="site-icon-remove-btn"
                                        >
                                            <X className="w-4 h-4 mr-2" />
                                            Supprimer l&apos;icone
                                        </Button>
                                    )}
                                    <p className="text-xs text-slate-500">PNG, JPG, WEBP ou SVG - 2 Mo max</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Nom *</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Nom du site"
                                data-testid="site-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Adresse</Label>
                            <Input
                                value={form.address}
                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                                placeholder="Adresse complète"
                                data-testid="site-address-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Contact</Label>
                            <Input
                                value={form.contact}
                                onChange={(e) => setForm({ ...form, contact: e.target.value })}
                                placeholder="Téléphone ou email"
                                data-testid="site-contact-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSave} className="btn-primary" data-testid="save-site-btn">
                            {editingSite ? 'Enregistrer' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer ce site ? Cette action est irréversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-500 hover:bg-red-600">
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
